/**
 * Listener GLOBAL de chamada entrante (montado no AppShell — sempre ativo,
 * lição #70: listener em página desmontável morre quando o user navega).
 *
 * `voip.call.incoming` chega na dept room (mesma audiência do card pendente).
 * Filtro local: chamada com dono (`target_user_id`) só toca pro dono e pra
 * admins. `voip.call.ended` para o toque — atendida em outro painel/celular,
 * recusada, perdida ou quem ligou desistiu.
 */
import { getSocket } from '@/features/atendimento/socket';
import { useAuthStore } from '@/shared/stores/auth-store';
import { useEffect } from 'react';
import { type IncomingCallPayload, useIncomingCallStore } from './incoming-call-store';
import { stopRingtone, startRingtone } from './ringtone';

interface CallEndedPayload {
  call_id: string;
  outcome: 'answered' | 'missed' | 'declined' | 'other';
  by_user_id?: number | null;
}

export function useIncomingCallListener(): void {
  useEffect(() => {
    const socket = getSocket();

    const onIncoming = (payload: IncomingCallPayload): void => {
      const user = useAuthStore.getState().user;
      if (!user) return;
      // Ticket com dono toca SÓ no dono (decisão de produto — admins não
      // tocam junto). Sem dono, toca em quem veria o card de pendente.
      if (payload.target_user_id && payload.target_user_id !== user.id) {
        return;
      }
      // Já tem uma tocando → ignora a segunda (1 chamada por vez no painel).
      if (useIncomingCallStore.getState().call) return;
      useIncomingCallStore.getState().setCall(payload);
      startRingtone();
    };

    const onEnded = (payload: CallEndedPayload): void => {
      const current = useIncomingCallStore.getState().call;
      if (current?.call_id !== payload.call_id) return;
      stopRingtone();
      useIncomingCallStore.getState().clear();
    };

    socket.on('voip.call.incoming', onIncoming as never);
    socket.on('voip.call.ended', onEnded as never);
    return () => {
      socket.off('voip.call.incoming', onIncoming as never);
      socket.off('voip.call.ended', onEnded as never);
      stopRingtone();
    };
  }, []);
}
